<template>
  <div>
    <Sidebar
      :menu-items="menuItems"
      :active-path="activePath"
      :width="280"
      :mini="!(!mini || mouseover)"
      :mini-to-overlay="mini"
      @mouseover="mouseover = true"
      @mouseout="mouseover = popup"
      @click="onClick"
    >
      <template v-slot:prepend>
        <div class="msp-logo-container" v-if="mspLogoUrl && (!mini || mouseover)">
          <img :src="mspLogoUrl" alt="MSP Logo" class="msp-logo" />
        </div>
        <Item>
          <ItemSection side>
            <Icon :name="`img:${vendor}-logo.svg`" />
          </ItemSection>

          <ItemSection>
            <Selector
              :value="vendor"
              :options="vendorOptions"
              :use-input="false"
              @change="onChangeVendor"
              @popup-show="popup = true"
              @popup-hide="popup = false"
            />
          </ItemSection>
        </Item>
        <Separator spaced />
      </template>
    </Sidebar>

    <AccessGroupDialog
      v-model="openAccessGroupDialog"
      :billingGroups="billingGroups"
      @chooseGroup="logOnToInvoice"
    />
  </div>
</template>

<script>
import { mapMutations, mapState, mapGetters, mapActions } from 'vuex';
import Cookies from 'js-cookie';
import Config from '@/config';
import { appEnv, locationOrigin } from '@/lib/navigator';
import { showInvoice } from '@/lib/tools';
import { vendorOptions } from '@/lib/vendors';

import Sidebar from '@design-system/layouts/Sidebar.vue';
import Selector from '@design-system/components/Selector';
import Separator from '@design-system/components/Separator';
import Icon from '@design-system/components/Icon.vue';
import Item from '@design-system/components/Item.vue';
import ItemSection from '@design-system/components/ItemSection.vue';
import AccessGroupDialog from '@/components/InvoiceDetails/AccessGroupDialog.vue';

const messages = {
  en: {
    Dashboard: 'Dashboard',
    'Usage Reports': 'Usage Reports',
    'Savings Plans': 'Savings Plans',
    'Usage Details': 'Invoice Details',
    Invoice: 'Invoice',
    Preferences: 'Preferences',
    Documents: 'Documents',
    'Changing vendor...': 'Changing vendor...',
    'Switched to {vendor}.': 'Switched to {vendor}.',
    'Failed to switch vendor.': 'Failed to switch vendor.',
    'Report Filters': 'Report Filters',
  },
  ja: {
    Dashboard: 'ダッシュボード',
    'Usage Reports': 'レポート',
    'Savings Plans': 'Savings Plans',
    'Usage Details': 'ご利用明細',
    Invoice: '請求書',
    Preferences: '環境設定',
    Documents: 'ドキュメント',
    'Changing vendor...': 'ベンダーを変更中...',
    'Switched to {vendor}.': '{vendor}に切り替えました。',
    'Failed to switch vendor.': 'ベンダーの切り替えに失敗しました。',
    'Report Filters': 'レポートフィルター',
  },
};

export default {
  name: 'SidebarNav',
  i18n: { messages },
  props: {
    mini: { type: Boolean },
    miniToOverlay: { type: Boolean },
  },
  components: { Sidebar, Selector, Separator, Icon, Item, ItemSection, AccessGroupDialog },
  data() {
    return {
      mouseover: false,
      popup: false,
      openAccessGroupDialog: false,
      mspLogoUrl: '', 
    };
  },
  computed: {
    ...mapState('user', {
      mspId: state => state.data.msp_id,
    }),

    ...mapState({
      companyId: state => state.userState.companyId,
      accessGroup: state => state.userState.accessGroup,
      readyMonth: state => state.readyMonth,
      vendor: state => state.vendor,
    }),

    ...mapState({
      userMeta: state => state.userMeta,
    }),

    ...mapGetters('user', {
      resourceTypes: 'getResourceTypes',
      locale: 'getLocale',
      billingGroups: 'getBillingGroups',
    }),

    vendorOptions() {
      return vendorOptions();
    },

    activePath() {
      return this.$route.path;
    },

    menuItems() {
      return [
        {
          key: 'dashboard',
          label: this.$t('Dashboard'),
          icon: 'o_dashboard',
          to: '/dashboard',
        },
        ...(this.displayUsageReportsMenu
          ? [
              {
                key: 'usage_reports',
                label: this.$t('Usage Reports'),
                icon: 'o_leaderboard',
              },
            ]
          : []),
        ...(this.displayReportFilters
          ? [
              {
                key: 'report_filters',
                label: this.$t('Report Filters'),
                icon: 'o_filter_alt',
              },
            ]
          : []),
        ...(this.$display('invoice') && this.$can('ReadInvoice')
          ? [
              {
                key: 'usage_details',
                label: this.$t('Usage Details'),
                icon: 'o_description',
              },
            ]
          : []),
        // ...(this.$flag('98124DA9-C197-4882-B339-AFB9259ECC21')
        ...(this.$display('aqua_link')
          ? [
              {
                key: 'aqua',
                label: this.$t('Aqua'),
                icon: `img:aqua-logo.svg`,
                separator: true,
              },
            ]
          : []),
        {
          key: 'preferences',
          label: this.$t('Preferences'),
          icon: 'o_settings',
          to: '/preferences',
        },
        {
          key: 'documents',
          label: this.$t('Documents'),
          icon: 'import_contacts',
          separator: true,
        },
      ];
    },

    displayUsageReportsMenu() {
      if (
        !this.$can('/usage_reports/account') &&
        !this.$can('/usage_reports/cross_tag') &&
        !this.$can('/usage_reports/group')
      ) {
        return false;
      }

      if (
        this.resourceTypes.includes('account') ||
        this.resourceTypes.includes('azure_subscription')
      ) {
        return this.$display('usage_account') || this.$display('usage_group');
      }

      if (this.resourceTypes.includes('tag')) {
        return this.$display('usage_crosstag');
      }
    },

    displayReportFilters() {
      return this.$display('wavepro_report_filters') && this.vendor === 'aws';
    },
  },
  async mounted() {
    try {
      this.mspLogoUrl = await this.getMspLogo(); 
    } catch (error) {
      console.error('Failed to load logo:', error);
    }
  },
  methods: {
    ...mapMutations(['updateLoadingState']),
    ...mapActions(['updateVendor']),

  getMspLogo() {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = `https://wave-pro-msplogo.s3.ap-northeast-1.amazonaws.com/${this.mspId}`;
      img.onload = () => resolve(img.src);
      img.onerror = () => reject('Image load error');
    });
  },

    async onChangeVendor(vendor) {
      try {
        if (vendor === this.vendor) return false;
        await this.$alpha.loading.show(this.$t('Changing vendor...'));

        await this.$router.push('/dashboard').catch(() => {});

        await this.updateVendor(vendor);

        await this.$alpha.loading.hide(1000);

        // Notification
        const formatName = name => {
          if (name === 'aws') return 'Amazon Web Services';
          if (name === 'azure') return 'Microsoft Azure';
          if (name === 'gcp') return 'Google Cloud';
        };
        await this.$alpha.notify.custom({
          message: this.$t('Switched to {vendor}.', { vendor: formatName(vendor) }),
          type: 'success',
        });
      } catch (e) {
        await this.$alpha.loading.hide(1000);

        await this.$alpha.notify.custom({
          message: this.$t('Failed to switch vendor.'),
          type: 'danger',
        });
      }
    },

    onClick(key) {
      switch (key) {
        case 'usage_reports':
          this.onGoToUsageReports();
          break;
        case 'report_filters':
          this.onGoToReportFilters();
          break;
        case 'usage_details':
          this.onLoginToInvoice();
          break;
        case 'aqua':
          this.onLoginToAqua();
          break;
        case 'documents':
          this.onOpenDocumentLink();
          break;
      }
    },

    onGoToUsageReports() {
      if (this.resourceTypes.includes('account') && this.$display('usage_account')) {
        // Resource types has 'account'.
        return this.$router.push('/usage_reports/account').catch(() => {});
      } else if (this.resourceTypes.includes('tag') && this.$display('usage_crosstag')) {
        // Resource types has 'tag'.
        return this.$router.push('/usage_reports/cross_tag').catch(() => {});
      } else if (this.$display('usage_group') && !this.resourceTypes.includes('tag')) {
        return this.$router.push('/usage_reports/group').catch(() => {});
      } else if (this.resourceTypes.includes('azure_subscription')) {
        return this.$router.push('/usage_reports/azure_subscription').catch(() => {});
      } else {
        return false;
      }
    },

    onGoToReportFilters() {
      return this.$router.push('/report_filters');
    },

    onLoginToInvoice() {
      if (this.accessGroup) {
        this.openAccessGroupDialog = true;
      } else {
        showInvoice(this.companyId, this.readyMonth);
      }
    },

    onLoginToAqua() {
      try {
        let accessToken = Cookies.get('mo_wave_for_reseller_access_token', {
          path: Config.cookiePath,
        });

        if (accessToken === 'undefined' || !accessToken) {
          accessToken = Cookies.get('mo_access_token', {
            path: Config.cookiePath,
          });
        }

        Cookies.set('mo_access_token', accessToken, {
          expires: Config.tokenExpire,
          path: Config.cookieAquaPath,
        });

        let base = locationOrigin;
        if (appEnv === 'localhost') {
          const port = Cookies.get('aqua_port');
          if (!port) throw new Error('Should set "port" on login page.');

          base = `${base}:${port}`;
        }

        window.open(`${base}${Config.cookieAquaPath}`, '_blank');
      } catch (err) {
        this.$message.error(this.$t('Login failed.'));
        console.error(err);
      }
    },

    onOpenDocumentLink() {
      let url;
      if (this.locale === 'ja') {
        url = 'https://labs.alphaus.cloud/wavedocs-ja/';
      } else {
        url = 'https://labs.alphaus.cloud/wavedocs/';
      }

      window.open(url, 'newtab');
    },
    logOnToInvoice(billingInternalId) {
      showInvoice(billingInternalId, this.readyMonth);
    },
  },
};
</script>

<style scoped>
.msp-logo-container {
  margin: 4px 4px 4px 16px;
  width: 248px; 
  height: 38px; 
  border-radius: 4px;
  box-shadow: rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px;
  display: flex; 
  align-items: center; 
  padding: 8px;
  justify-content: center; 
  overflow: hidden; 
}

.msp-logo {
  max-width: 50%; 
  height: auto; 
}

</style>

